import { MessageSquare, PenTool, FileText, Box, ClipboardCheck, HardHat } from 'lucide-react';
import { useTranslation, useScrollAnimation, useStaggerAnimation } from '../hooks';

const Process = () => {
  const { t } = useTranslation();

  // Icônes pour chaque étape
  const stepIcons = [
    MessageSquare,  // Consultation
    PenTool,        // Étude & Esquisses
    FileText,       // Plans Techniques
    Box,            // Rendus 3D
    ClipboardCheck, // Permis de Construire
    HardHat         // Suivi de Chantier
  ];

  // Animation pour le titre
  const { ref: titleRef, isVisible: isTitleVisible } = useScrollAnimation({
    threshold: 0.2,
    rootMargin: '0px 0px -100px 0px'
  });

  // Animation stagger pour les étapes
  const { containerRef, getItemStyle } = useStaggerAnimation(t.process.steps.length, {
    staggerDelay: 150,
    threshold: 0.1
  });

  return (
    <section id="process" className="py-24 bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Titre avec animation */}
        <div
          ref={titleRef}
          className="text-center mb-20"
          style={{
            opacity: isTitleVisible ? 1 : 0,
            transform: isTitleVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
          }}
        >
          <h2 className="text-4xl lg:text-5xl font-light mb-6 tracking-wide">
            {t.process.title}
          </h2>
          <p
            className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed"
            style={{
              opacity: isTitleVisible ? 1 : 0,
              transform: isTitleVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s'
            }}
          >
            {t.process.subtitle}
          </p>
        </div>

        {/* Timeline des étapes */}
        <div ref={containerRef} className="relative">
          {/* Ligne de connexion - Desktop */}
          <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gray-700">
            <div
              className="h-full bg-white transition-all duration-1000"
              style={{
                width: isTitleVisible ? '100%' : '0%',
                transitionDelay: '0.5s'
              }}
            />
          </div>

          {/* Grille des étapes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-4">
            {t.process.steps.map((step, index) => {
              const Icon = stepIcons[index];
              return (
                <div
                  key={index}
                  style={getItemStyle(index)}
                  className="relative text-center group"
                >
                  {/* Cercle numéroté */}
                  <div className="relative z-10 mx-auto mb-6">
                    <div className="w-32 h-32 lg:w-28 lg:h-28 rounded-full border-2 border-gray-600 group-hover:border-white transition-colors duration-300 flex items-center justify-center bg-black mx-auto">
                      <div className="text-center">
                        <Icon
                          size={32}
                          className="mx-auto mb-2 text-gray-400 group-hover:text-white transition-colors duration-300"
                        />
                        <span className="text-2xl font-light text-white">
                          {step.number.toString().padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contenu */}
                  <h3 className="text-lg font-semibold mb-3 tracking-wide group-hover:text-white transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors">
                    {step.description}
                  </p>

                  {/* Flèche de connexion - Mobile/Tablet */}
                  {index < t.process.steps.length - 1 && (
                    <div className="lg:hidden flex justify-center my-4">
                      <div className="w-0.5 h-8 bg-gray-700" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
